import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import SplineApi from "../../tools/spline-api.js";

export interface PrimaryErrorEntry {
  // Identity and model output, rendered by the Primary Errors table.
  issue_id: number;
  cusip: string;
  coupon: number;
  offering_date: string;
  issuer_long_name: string;
  state: string;
  spline_lt_rating_composite: string;
  maturity_date: string;
  offering_yield: number;
  predicted_yield: number;
  predicted_spread: number;
  error: number;

  // Deal parameters, used to load a whole issue into the deal builder. The
  // response isn't validated at runtime, and these come from a left join on
  // the backend, so treat every one of them as possibly missing.
  issue_description: string | null;
  state_long: string | null;
  security_code: string | null;
  tax_designation: string | null;
  offering_type_long: string | null;
  underlying_rating_str: string | null;
  state_enhancement: number | null;
  insurance: number | null;
  total_offering_amount: number | null;
  denomination_amount: number | null;
  dated_date: string | null;
  delivery_date: string | null;
  first_coupon_date: string | null;
  optional_call_flag: string | null;
  next_call_date: string | null;
  total_maturity_offering_amt: number | null;
  sink_fund_type: string | null;
  sinkable: number | null;
}

export default function useAnalysis() {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<PrimaryErrorEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchErrors = async (payload: { offering_date?: string; cusip?: string }) => {
    setLoading(true);
    setError(null);
    setData([]);

    try {
      const token = await getAccessTokenSilently();
      const api = new SplineApi(token);
      const response = await api.getPrimaryErrors(payload);
      setData(response as PrimaryErrorEntry[]);
    } catch (e: any) {
      setError(e?.message || "Failed to fetch primary errors.");
    } finally {
      setLoading(false);
    }
  };

  return { fetchErrors, loading, data, error };
}
